import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getRevenueData, type PayStatus, type RevenueRow } from '../../lib/api';
import {
  C, TONES, num, inr, inrShort, plural, pctOf, norm, fmtDay, fmtRange, monthName, shiftMonth, addDays, todayIST, within,
  feeTotals, distinctNames, downloadCsv, presetPeriod, periodFromParams, periodParams,
  type Period, type ActiveFilter, type Option, type Tone,
  ReportHeader, HeaderButton, PeriodPicker, SelectFilter, SearchFilter, FilterCard, Section, Card, KpiTile, HowCounted,
  ColumnChart, StackBar, MiniMeter, Pill, Notice, Loading, ServerOutdated, ToggleChip,
} from '../../components/ReportKit';

const PAY: { key: string; status: PayStatus; label: string; tone: Tone }[] = [
  { key: 'paid',    status: 'Fully Paid',  label: 'Fully paid',  tone: 'good' },
  { key: 'owing',   status: 'Partly Paid', label: 'Still owes',  tone: 'warning' },
  { key: 'nofee',   status: 'Fee not set', label: 'Fee not set', tone: 'neutral' },
  { key: 'ranaway', status: 'Ran Away',    label: 'Ran away',    tone: 'critical' },
];
const payOf = (s: PayStatus) => PAY.find(p => p.status === s)!;

const STUDENT_STATUS: Option[] = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Ran Away', label: 'Ran away' },
  { value: 'Institute Leave', label: 'Institute leave' },
  { value: 'none', label: 'Not with a mentor yet' },
];

type GroupBy = 'course' | 'mode' | 'sales' | 'mentor' | 'month';
const GROUPS: { key: GroupBy; label: string; column: string }[] = [
  { key: 'course', label: 'Course', column: 'Course' },
  { key: 'mode', label: 'Online / offline', column: 'Mode' },
  { key: 'sales', label: 'Salesperson', column: 'Salesperson' },
  { key: 'mentor', label: 'Mentor', column: 'Mentor' },
  { key: 'month', label: 'Month joined', column: 'Month joined' },
];

type SortCol = 'name' | 'course' | 'mode' | 'joined' | 'fee' | 'paid' | 'balance' | 'status';
const SORT_VALUE: Record<SortCol, (r: RevenueRow) => string | number> = {
  name: r => norm(r.name), course: r => norm(r.course), mode: r => r.mode, joined: r => r.joined,
  fee: r => r.courseFee, paid: r => r.totalPaid, balance: r => r.balance, status: r => PAY.findIndex(p => p.status === r.payStatus),
};

const modeLabel = (m: string) => (m === 'online' ? 'Online' : 'Offline');
const lastDayOf = (month: string) => addDays(`${shiftMonth(month, 1)}-01`, -1);

const SortHeader: React.FC<{ col: SortCol; sortCol: SortCol; asc: boolean; onSort: (c: SortCol) => void; right?: boolean; children: React.ReactNode }> = ({ col, sortCol, asc, onSort, right, children }) => (
  <th className={`px-4 py-2.5 whitespace-nowrap ${right ? 'text-right' : ''}`} aria-sort={sortCol === col ? (asc ? 'ascending' : 'descending') : undefined}>
    <button onClick={() => onSort(col)} className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-800 ${sortCol === col ? 'text-gray-800' : ''}`}>
      {children}<span className="text-[9px]">{sortCol === col ? (asc ? '▲' : '▼') : '↕'}</span>
    </button>
  </th>
);

// ── main page ─────────────────────────────────────────────────────────────────
// Every payment record, filtered here: when the student joined, course,
// online / offline, salesperson, mentor, student status, payment status and
// course stage, plus a search. Totals, the breakdowns and the table all
// follow the filters; the filters are kept in the page address.

const AdminRevenue: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [params, setParams] = useSearchParams();
  const get = (k: string) => params.get(k) || '';

  const [period, setPeriod]   = useState<Period>(() => periodFromParams(params, 'all'));
  const [course, setCourse]   = useState(() => get('course'));
  const [mode, setMode]       = useState(() => get('mode'));
  const [sales, setSales]     = useState(() => get('sales'));
  const [mentor, setMentor]   = useState(() => get('mentor'));
  const [sstatus, setSstatus] = useState(() => get('student'));
  const [pay, setPay]         = useState(() => get('pay'));
  const [stage, setStage]     = useState(() => get('stage'));
  const [search, setSearch]   = useState(() => get('q'));
  const [by, setBy]           = useState<GroupBy>(() => (GROUPS.some(g => g.key === get('by')) ? get('by') as GroupBy : 'course'));
  const [sortCol, setSortCol] = useState<SortCol>(() => (get('sort') in SORT_VALUE ? get('sort') as SortCol : 'joined'));
  const [sortAsc, setSortAsc] = useState(() => get('dir') === 'asc');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage]       = useState(0);

  const [rows, setRows]         = useState<RevenueRow[] | null>(null);
  const [loading, setLoading]   = useState(true);
  const [outdated, setOutdated] = useState(false);
  const today = todayIST();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRevenueData(adminPassword);
      const fresh = res.rows.every(r => !!r.payStatus);
      setOutdated(!fresh);
      if (fresh) setRows(res.rows);
    } catch (err: any) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const next = new URLSearchParams(periodParams(period));
    const put = (k: string, v: string) => { if (v) next.set(k, v); };
    put('course', course); put('mode', mode); put('sales', sales); put('mentor', mentor); put('student', sstatus);
    put('pay', pay); put('stage', stage); put('q', search);
    if (by !== 'course') next.set('by', by);
    if (sortCol !== 'joined') next.set('sort', sortCol);
    if (sortAsc) next.set('dir', 'asc');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [period, course, mode, sales, mentor, sstatus, pay, stage, search, by, sortCol, sortAsc, params, setParams]);

  // ── filter choices, from the records ──
  const all = useMemo(() => rows ?? [], [rows]);
  const courseNames = useMemo(() => distinctNames(all.map(r => r.course)), [all]);
  const courseLabel = useCallback((c: string) => courseNames.find(n => norm(n) === norm(c)) || c.trim() || 'No course', [courseNames]);
  const salesNames  = useMemo(() => distinctNames(all.map(r => r.salesPerson)), [all]);
  const mentorOptions = useMemo(() => {
    const seen: Record<string, string> = {};
    all.forEach(r => { if (r.mentorId) seen[r.mentorId] = r.mentorName || r.mentorId; });
    return Object.keys(seen).map(id => ({ value: id, label: seen[id] })).sort((a, b) => a.label.localeCompare(b.label));
  }, [all]);

  const filtered = useMemo(() => {
    const q = norm(search);
    const payStatus = PAY.find(p => p.key === pay)?.status;
    return all.filter(r =>
      (!period.from || within(r.joined, period))
      && (!course || norm(r.course) === norm(course))
      && (!mode || r.mode === mode)
      && (!sales || (sales === 'none' ? !r.salesPerson.trim() : norm(r.salesPerson) === norm(sales)))
      && (!mentor || (mentor === 'none' ? !r.mentorId : r.mentorId === mentor))
      && (!sstatus || (sstatus === 'none' ? !r.studentStatus : r.studentStatus === sstatus))
      && (!payStatus || r.payStatus === payStatus)
      && (!stage || (stage === 'classover' ? r.classOver : !r.classOver))
      && (!q || [r.name, r.email, r.phone, r.course, r.salesPerson].some(v => norm(v).includes(q))));
  }, [all, period, course, mode, sales, mentor, sstatus, pay, stage, search]);

  useEffect(() => { setPage(0); }, [filtered, pageSize, sortCol, sortAsc]);

  const t = useMemo(() => feeTotals(filtered), [filtered]);

  // Fees by joining month — up to 12 months, ending with the period
  const months = useMemo(() => {
    const end = period.to.slice(0, 7);
    let start = shiftMonth(end, -11);
    if (period.from && period.from.slice(0, 7) > start) start = period.from.slice(0, 7);
    const list: string[] = [];
    for (let m = start; m <= end; m = shiftMonth(m, 1)) list.push(m);
    return list.map(m => ({ month: m, t: feeTotals(filtered.filter(r => r.joined.startsWith(m))) }));
  }, [filtered, period]);

  // Breakdown by the chosen grouping
  const groups = useMemo(() => {
    const keyOf: Record<GroupBy, (r: RevenueRow) => [string, string]> = {
      course: r => [norm(r.course), courseLabel(r.course)],
      mode:   r => [r.mode, modeLabel(r.mode)],
      sales:  r => [norm(r.salesPerson), r.salesPerson.trim() ? (salesNames.find(n => norm(n) === norm(r.salesPerson)) || r.salesPerson) : 'No salesperson'],
      mentor: r => [r.mentorId, r.mentorId ? r.mentorName || r.mentorId : 'Not with a mentor yet'],
      month:  r => (r.joined ? [r.joined.slice(0, 7), monthName(r.joined.slice(0, 7))] : ['', 'Joining date unknown']),
    };
    const map: Record<string, { key: string; label: string; rows: RevenueRow[] }> = {};
    filtered.forEach(r => {
      const [k, label] = keyOf[by](r);
      (map[k] = map[k] || { key: k, label, rows: [] }).rows.push(r);
    });
    const list = Object.keys(map).map(k => ({ ...map[k], t: feeTotals(map[k].rows) }));
    return by === 'month' ? list.sort((a, b) => b.key.localeCompare(a.key)) : list.sort((a, b) => b.t.total - a.t.total);
  }, [filtered, by, courseLabel, salesNames]);

  const applyGroup = (g: { key: string; label: string }) => {
    if (by === 'course') { if (g.key) setCourse(g.label); }
    else if (by === 'mode') setMode(g.key);
    else if (by === 'sales') setSales(g.key ? g.label : 'none');
    else if (by === 'mentor') setMentor(g.key || 'none');
    else if (g.key) setPeriod({ preset: 'custom', from: `${g.key}-01`, to: lastDayOf(g.key) < today ? lastDayOf(g.key) : today });
  };
  const groupIsFiltered = (g: { key: string; label: string }) =>
    (by === 'course' && !!course && norm(course) === g.key) || (by === 'mode' && mode === g.key)
    || (by === 'sales' && !!sales && (sales === 'none' ? g.key === '' : norm(sales) === g.key))
    || (by === 'mentor' && !!mentor && (mentor === 'none' ? g.key === '' : mentor === g.key));

  const sorted = useMemo(() => {
    const val = SORT_VALUE[sortCol];
    return [...filtered].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = x < y ? -1 : x > y ? 1 : 0;
      return (sortAsc ? c : -c) || norm(a.name).localeCompare(norm(b.name));
    });
  }, [filtered, sortCol, sortAsc]);
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const shown = sorted.slice(page * pageSize, page * pageSize + pageSize);

  const sortBy = (c: SortCol) => {
    if (c === sortCol) setSortAsc(a => !a);
    else { setSortCol(c); setSortAsc(c === 'name' || c === 'course' || c === 'mode' || c === 'status'); }
  };

  const mentorLabel = mentor === 'none' ? 'Not with a mentor yet' : mentorOptions.find(m => m.value === mentor)?.label || mentor;
  const active: ActiveFilter[] = [
    ...(period.from ? [{ label: `Joined: ${fmtRange(period.from, period.to)}`, onRemove: () => setPeriod(presetPeriod('all')) }] : []),
    ...(course ? [{ label: `Course: ${courseLabel(course)}`, onRemove: () => setCourse('') }] : []),
    ...(mode ? [{ label: modeLabel(mode), onRemove: () => setMode('') }] : []),
    ...(sales ? [{ label: `Salesperson: ${sales === 'none' ? 'none' : sales}`, onRemove: () => setSales('') }] : []),
    ...(mentor ? [{ label: `Mentor: ${mentorLabel}`, onRemove: () => setMentor('') }] : []),
    ...(sstatus ? [{ label: `Student: ${STUDENT_STATUS.find(s => s.value === sstatus)?.label || sstatus}`, onRemove: () => setSstatus('') }] : []),
    ...(pay ? [{ label: `Payment: ${PAY.find(p => p.key === pay)?.label || pay}`, onRemove: () => setPay('') }] : []),
    ...(stage ? [{ label: stage === 'classover' ? 'Class over' : 'Still studying', onRemove: () => setStage('') }] : []),
    ...(search ? [{ label: `Search: “${search}”`, onRemove: () => setSearch('') }] : []),
  ];
  const clearAll = () => {
    setPeriod(presetPeriod('all'));
    setCourse(''); setMode(''); setSales(''); setMentor(''); setSstatus(''); setPay(''); setStage(''); setSearch('');
  };

  const exportCsv = () => downloadCsv(`revenue-${today}.csv`,
    ['Name', 'Email', 'Phone', 'Course', 'Mode', 'Joined', 'Course fee', 'Registration paid', 'Total paid', 'Still owes', 'Lost (ran away)', 'Payment status', 'Class over', 'Student status', 'Mentor', 'Salesperson'],
    sorted.map(r => {
      const ranAway = r.payStatus === 'Ran Away';
      return [r.name, r.email, r.phone, r.course, modeLabel(r.mode), r.joined, r.courseFee, r.regPaid, r.totalPaid,
        ranAway ? 0 : r.balance, ranAway ? r.balance : 0, payOf(r.payStatus).label, r.classOver ? 'Yes' : 'No',
        r.studentStatus || 'Not with a mentor yet', r.mentorName, r.salesPerson];
    }));
  const sortProps = { sortCol, asc: sortAsc, onSort: sortBy };

  return (
    <TrackerLayout>
      <div className="space-y-6">
        <ReportHeader icon="💰" title="Revenue"
          intro="Course fees: what's been collected and what students still owe. Filter by when students joined, course, online or offline, salesperson and more — every number and the table follow your filters."
          actions={<>
            <HeaderButton onClick={exportCsv} disabled={!rows || filtered.length === 0}>⬇ Export CSV</HeaderButton>
            <HeaderButton onClick={load}>↻ Refresh</HeaderButton>
          </>} />

        <FilterCard busy={loading && !!rows} active={active} onClearAll={clearAll}
          showing={<>Showing <b className="text-gray-700">{rows ? plural(filtered.length, 'student') : '…'}</b>{rows && filtered.length !== all.length && <> of {num(all.length)}</>}</>}>
          <PeriodPicker label="Joined" value={period} onChange={setPeriod} presets={['all', 'month', 'lastMonth', '90d', 'year', 'custom']} />
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter label="Course" value={course} onChange={setCourse} allLabel="All courses"
              options={courseNames.map(c => ({ value: c, label: c }))} />
            <SelectFilter label="Online / offline" value={mode} onChange={setMode} allLabel="Both"
              options={[{ value: 'online', label: 'Online' }, { value: 'offline', label: 'Offline' }]} />
            <SelectFilter label="Salesperson" value={sales} onChange={setSales} allLabel="All salespeople"
              options={[...salesNames.map(s => ({ value: s, label: s })), { value: 'none', label: 'No salesperson' }]} />
            <SelectFilter label="Mentor" value={mentor} onChange={setMentor} allLabel="All mentors"
              options={[...mentorOptions, { value: 'none', label: 'Not with a mentor yet' }]} />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter label="Payment" value={pay} onChange={setPay} allLabel="Any payment status"
              options={PAY.map(p => ({ value: p.key, label: p.label }))} />
            <SelectFilter label="Student status" value={sstatus} onChange={setSstatus} allLabel="Any student status" options={STUDENT_STATUS} />
            <SelectFilter label="Course stage" value={stage} onChange={setStage} allLabel="Studying or class over"
              options={[{ value: 'studying', label: 'Still studying' }, { value: 'classover', label: 'Class over' }]} />
            <SearchFilter label="Search" value={search} onChange={setSearch} placeholder="Name, phone, email, course or salesperson…" />
          </div>
        </FilterCard>

        {outdated && <ServerOutdated />}

        {!rows ? (loading ? <Loading text="Loading revenue…" /> : null) : (
          <div className={`space-y-8 transition-opacity ${loading ? 'opacity-60' : ''}`}>

            <Section title="Totals" caption={`Students matching the filters · ${period.from ? `joined ${fmtRange(period.from, period.to)}` : 'joined any time'}`}>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KpiTile label="Students" value={num(t.students)}
                  hint={[
                    `${num(t.fullyPaid)} fully paid`, `${num(t.owing)} still owe`,
                    t.lostStudents ? `${num(t.lostStudents)} ran away` : '', t.feeNotSet ? `${num(t.feeNotSet)} fee not set` : '',
                  ].filter(Boolean).join(' · ')} />
                <KpiTile label="Collected" value={inr(t.collected)}
                  hint={`${pctOf(t.collected, t.total) ?? 0}% of total fees, including ${inr(t.regCollected)} in registration fees`} />
                <KpiTile label="Still to collect" value={inr(t.outstanding)}
                  hint={`Owed by ${plural(t.owing, 'student')} who haven't paid in full`}
                  onClick={pay === 'owing' ? undefined : () => setPay('owing')} cta="Show them" />
                <KpiTile label="Lost — ran away" value={inr(t.lost)}
                  hint={`Unpaid fees of ${plural(t.lostStudents, 'student')} marked Ran Away`}
                  onClick={pay === 'ranaway' || t.lostStudents === 0 ? undefined : () => setPay('ranaway')} cta="Show them" />
                <KpiTile label="Total fees" value={inr(t.total)} hint="Collected + still to collect + lost" />
              </div>
              <Card>
                <StackBar format={inr} columns="grid-cols-1 sm:grid-cols-3" parts={[
                  { label: 'Collected', value: t.collected, color: C.good },
                  { label: 'Still to collect', value: t.outstanding, color: C.warning },
                  { label: 'Lost — ran away', value: t.lost, color: C.critical },
                ]} />
                {t.feeNotSet > 0 && pay !== 'nofee' && (
                  <div className="mt-4">
                    <Notice tone="warning" action={<button onClick={() => setPay('nofee')} className="text-xs font-bold underline">Show them</button>}>
                      {plural(t.feeNotSet, 'student has', 'students have')} no course fee set, so what they owe isn't counted. Set it on the Payments page.
                    </Notice>
                  </div>
                )}
              </Card>
            </Section>

            <Section title="Fees by month joined" caption={`Up to 12 months${period.from ? ` in ${fmtRange(period.from, period.to)}` : ''} — each column is the students who joined that month`}>
              <Card>
                <ColumnChart firstColumn="Month joined" format={inr} axisFormat={inrShort}
                  series={[{ name: 'Collected', color: C.good }, { name: 'Still to collect', color: C.warning }, { name: 'Lost — ran away', color: C.critical }]}
                  points={months.map(m => ({
                    key: m.month, label: monthName(m.month).replace(/ (\d\d)(\d\d)$/, ' $2'), title: `Joined in ${monthName(m.month)} · ${plural(m.t.students, 'student')}`,
                    values: [m.t.collected, m.t.outstanding, m.t.lost],
                  }))}
                  note={p => { const m = months.find(x => x.month === p.key); return m && m.t.total ? `${pctOf(m.t.collected, m.t.total)}% collected` : null; }}
                  noteLabel="Collected" emptyText="No fees for students who joined in these months" />
              </Card>
            </Section>

            <Section title="Breakdown" caption="Click a row to filter by it">
              <Card flush>
                <div className="flex flex-wrap items-center gap-1.5 px-5 pt-4 pb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mr-1">Group by</span>
                  {GROUPS.map(g => <ToggleChip key={g.key} on={by === g.key} onClick={() => setBy(g.key)}>{g.label}</ToggleChip>)}
                </div>
                {groups.length === 0 ? <p className="text-center py-10 text-sm text-gray-400">No students match these filters</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          <th className="px-4 py-2.5">{GROUPS.find(g => g.key === by)!.column}</th>
                          <th className="px-4 py-2.5 text-right">Students</th>
                          <th className="px-4 py-2.5 text-right">Collected</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Still to collect</th>
                          <th className="px-4 py-2.5 text-right">Lost</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Total fees</th>
                          <th className="px-4 py-2.5 whitespace-nowrap">Collected %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map(g => {
                          const on = groupIsFiltered(g);
                          return (
                            <tr key={g.key || '—'} onClick={() => !on && applyGroup(g)}
                              className={`border-t border-gray-50 ${on ? 'bg-purple-50/60' : 'cursor-pointer hover:bg-gray-50'}`}
                              title={on ? 'Already filtered by this' : `Filter by ${g.label}`}>
                              <td className="px-4 py-2.5 font-semibold text-gray-800">{g.label}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{num(g.t.students)}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{inr(g.t.collected)}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{inr(g.t.outstanding)}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{g.t.lost ? inr(g.t.lost) : '—'}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-800">{inr(g.t.total)}</td>
                              <td className="px-4 py-2.5"><MiniMeter pct={pctOf(g.t.collected, g.t.total)} color={C.good} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </Section>

            <Section title="Students" caption={`${plural(filtered.length, 'student')} · click a column heading to sort`}>
              <Card flush>
                {filtered.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-sm font-semibold text-gray-500">No students match these filters</p>
                    {active.length > 0 && <button onClick={clearAll} className="mt-2 text-xs font-bold underline text-gray-500">Clear filters</button>}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500">
                            <SortHeader col="name" {...sortProps}>Student</SortHeader>
                            <SortHeader col="course" {...sortProps}>Course</SortHeader>
                            <SortHeader col="mode" {...sortProps}>Mode</SortHeader>
                            <SortHeader col="joined" {...sortProps}>Joined</SortHeader>
                            <SortHeader col="fee" right {...sortProps}>Course fee</SortHeader>
                            <SortHeader col="paid" right {...sortProps}>Paid</SortHeader>
                            <SortHeader col="balance" right {...sortProps}>Still owes</SortHeader>
                            <SortHeader col="status" {...sortProps}>Status</SortHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {shown.map(r => {
                            const p = payOf(r.payStatus);
                            const paidPct = r.courseFee > 0 ? Math.min(100, Math.round((r.totalPaid / r.courseFee) * 100)) : null;
                            return (
                              <tr key={r._id} className="border-t border-gray-50 align-top">
                                <td className="px-4 py-2.5">
                                  <p className="font-semibold text-gray-800">{r.name}</p>
                                  <p className="text-[11px] text-gray-400">{[r.phone, r.email].filter(Boolean).join(' · ') || 'No phone or email'}</p>
                                  {(r.mentorName || r.salesPerson) && (
                                    <p className="text-[11px] text-gray-400">{[r.mentorName && `Mentor: ${r.mentorName}`, r.salesPerson && `Sales: ${r.salesPerson}`].filter(Boolean).join(' · ')}</p>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-gray-600">{r.course || '—'}</td>
                                <td className="px-4 py-2.5 text-xs text-gray-600">{modeLabel(r.mode)}</td>
                                <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDay(r.joined)}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 whitespace-nowrap">
                                  {r.courseFee > 0 ? inr(r.courseFee) : <span className="text-xs font-semibold text-amber-700">Not set</span>}
                                </td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                  <p className="tabular-nums font-semibold text-gray-800">{inr(r.totalPaid)}</p>
                                  {paidPct != null && (
                                    <span className="inline-block mt-1 h-1 w-16 rounded-full bg-gray-100 overflow-hidden align-middle" title={`${paidPct}% of the course fee`}>
                                      <span className="block h-full rounded-full" style={{ width: `${paidPct}%`, background: TONES[p.tone].mark }} />
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                                  {r.balance > 0
                                    ? <span className={r.payStatus === 'Ran Away' ? 'text-gray-400 line-through' : 'font-bold text-red-700'}
                                        title={r.payStatus === 'Ran Away' ? 'Counted as lost' : undefined}>{inr(r.balance)}</span>
                                    : <span className="text-gray-300">—</span>}
                                </td>
                                <td className="px-4 py-2.5">
                                  <div className="flex flex-wrap gap-1">
                                    <Pill tone={p.tone}>{p.label}</Pill>
                                    {r.classOver && <Pill tone="neutral" icon={false}>Class over</Pill>}
                                    {r.studentStatus && r.studentStatus !== 'Active' && r.studentStatus !== 'Ran Away' && <Pill tone="neutral" icon={false}>{r.studentStatus}</Pill>}
                                    {!r.studentStatus && <Pill tone="neutral" icon={false}>Not with a mentor yet</Pill>}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-gray-100 bg-gray-50/60 text-xs">
                            <td className="px-4 py-2.5 font-bold text-gray-700" colSpan={4}>Total · {plural(filtered.length, 'student')}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-700">{inr(filtered.reduce((s, r) => s + r.courseFee, 0))}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-700">{inr(t.collected)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums font-bold text-red-700">{inr(t.outstanding)}</td>
                            <td className="px-4 py-2.5 text-gray-400">{t.lost > 0 && `+ ${inr(t.lost)} lost`}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
                      <span>{num(page * pageSize + 1)}–{num(Math.min(sorted.length, (page + 1) * pageSize))} of {num(sorted.length)}</span>
                      <div className="flex items-center gap-2">
                        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                          className="px-2 py-1 border border-gray-200 rounded-lg bg-white text-xs">
                          {[25, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
                        </select>
                        <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                          className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white disabled:opacity-40">← Prev</button>
                        <span>Page {page + 1} of {pages}</span>
                        <button onClick={() => setPage(p => p + 1)} disabled={page >= pages - 1}
                          className="px-2.5 py-1 rounded-lg border border-gray-200 bg-white disabled:opacity-40">Next →</button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            </Section>

            <HowCounted items={[
              ['Joined', "The joining date on the student's payment record (or when the record was made, if it has none). The Joined filter picks students by it — the money shown is everything they've paid so far, not only what was paid in those dates."],
              ['Collected', 'Everything paid so far — registration fee and balance payments.'],
              ['Still to collect', "Course fee − paid, for students who haven't paid in full. Ran Away students aren't included."],
              ['Lost — ran away', 'The unpaid fees of students marked Ran Away.'],
              ['Total fees', 'Collected + still to collect + lost.'],
              ['Fee not set', "A payment record with no course fee. What they've paid is counted; what they owe can't be until the fee is set on the Payments page."],
              ['Pay After Placement', 'For group-session students the course fee is the part paid before placement — ₹15,000 online, ₹20,000 offline. A student keeps the fee they joined at.'],
              ['Online / offline', "As set on the Payments page; otherwise from the student's sign-up (1:1 sessions are online, group batches offline unless they signed up for online)."],
              ['Mentor / Student status', 'From the student roster, matched to the payment by email (or by name when there is no email). Not with a mentor yet = not on the roster.'],
            ]} />
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminRevenue;
